import {
  getCampaignLinkStats,
  getDestinations,
  getMetricsForCampaign,
  getStatsForTimeRange,
  updateCampaignLabels,
} from 'api/CampaignAnalyticsAPI';
import { call, put, takeLatest } from 'redux-saga/effects';

import { addNegativeToast } from 'modules/toastContainer';
import { addToRootSaga } from 'store';
import { getErrorResponse } from 'utils/xhr';
import * as actions from './actions';
import * as actionTypes from './actionTypes';
import { processStatsForTimerangeResponse } from './utils';

/**
 * fetchStatsByTimeRangeSaga
 * Saga for fetching campaign analytics stats
 * @return { void }
 */

export function* fetchStatsByTimeRangeSaga({ requestData }) {
  try {
    const {
      campaignId,
      startTime,
      endTime,
      selectedMetrics,
      messageMedium,
      conversions,
      templateId,
    } = requestData;
    const data = yield call(getStatsForTimeRange, {
      campaignIds: [parseInt(campaignId, 10)],
      startTime,
      endTime,
      selectedMetrics,
      templateId,
    });
    yield put(
      actions.fetchStatsForTimerangeResponse(
        processStatsForTimerangeResponse(
          messageMedium,
          conversions.map(({ events }) => events?.[0]?.name),
          data,
        ),
      ),
    );
  } catch (error) {
    const err = getErrorResponse(error);
    yield put(actions.fetchStatsForTimerangeError(err));
    const message = `Please try again later. If the problem persists, contact your CSM.`;
    yield put(addNegativeToast(message));
  }
}

export function* updateLabels({ requestData }) {
  try {
    const { campaignId, labels, callback } = requestData;
    const response = yield call(updateCampaignLabels, campaignId, {
      labels,
    });
    yield put(actions.updateCampaignLabelsSuccess(response.data));
    callback();
  } catch (error) {
    yield put(actions.updateCampaignLabelsError(error));
    const message = `Please try again later. If the problem persists, contact your CSM.`;
    yield put(addNegativeToast(message));
  }
}

export function* fetchCampaignLinkStats({ requestData }) {
  try {
    const { campaignId, startDate, endDate, urlOnlyView, templateId } = requestData;
    const response = yield call(getCampaignLinkStats, {
      id: campaignId,
      startDate,
      endDate,
      urlOnlyView,
      templateId,
    });
    yield put(actions.fetchCampaignLinkStatsResponse(response));
  } catch (error) {
    yield put(actions.fetchCampaignLinkStatsError(error));
    const message = `Please try again later. If the problem persists, contact your CSM.`;
    yield put(addNegativeToast(message));
  }
}

export function* fetchMetricsForCampaignSaga({ queryData }) {
  try {
    const MetricsForCampaignResponse = yield call(getMetricsForCampaign, queryData);
    yield put(actions.fetchMetricsForCampaignResponse(MetricsForCampaignResponse));
  } catch (error) {
    yield put(actions.fetchMetricsForCampaignError(error.message));
  }
}

export function* fetchDestinationsSaga() {
  try {
    const { results } = yield call(getDestinations);
    yield put(actions.fetchDestinationsResponse(results));
  } catch (error) {
    yield put(actions.fetchDestinationsError(error.message));
  }
}

addToRootSaga([
  takeLatest(actionTypes.UPDATE_CAMPAIGN_ANALYTICS_LABELS_REQUEST, updateLabels),
  takeLatest(actionTypes.FETCH_STATS_FOR_TIMERANGE_REQUEST, fetchStatsByTimeRangeSaga),
  takeLatest(actionTypes.FETCH_CAMPAIGN_LINK_STATS_REQUEST, fetchCampaignLinkStats),
  takeLatest(actionTypes.FETCH_METRICS_FOR_CAMPAIGN_REQUEST, fetchMetricsForCampaignSaga),
  takeLatest(actionTypes.FETCH_DESTINATIONS_REQUEST, fetchDestinationsSaga),
]);
